import { useState, useEffect, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
} from '../../../services/session.js';
import { apiMqtt } from '../../../services/api.js';
import { iAmHigherAuthority } from '../../../libs.jsx';
import {
    fetchCheckinWindows,
    fetchRoundsWindows,
    fetchResidentGroups,
    fetchAreaGroups,
    addCheckinWindow,
    editCheckinWindows,
    editCheckinGroups,
    addRoundsWindow,
    editRoundsWindows,
    editRoundsGroups,
    deleteWindows,
    fetchEscalationData,
    fetchEventCategories,
    fetchCampusSettings,
    fetchDevices,
    fetchMobileGroups,
    editGlobalOutputs,
    addCareflow,
    editCareflow,
    deleteCareflows,
    editCareflowOutput,
} from './api/wellnessSettingsApi.js';

import ConfirmTypedDialog from '../../../components/v2/ConfirmTypedDialog.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import { describeWellnessDelete } from './components/wellnessDeleteCopy.js';
import CheckinTab from './components/CheckinTab.jsx';
import RoundsTab from './components/RoundsTab.jsx';
import WindowDrawer from './components/WindowDrawer.jsx';
import GroupAssignDrawer from './components/GroupAssignDrawer.jsx';
import CareflowTab from './components/CareflowTab.jsx';
import CareflowDrawer from './components/CareflowDrawer.jsx';
import DeviceAssignDrawer from '../../../components/v2/DeviceAssignDrawer.jsx';
import GlobalOutputDrawer from './components/GlobalOutputDrawer.jsx';
import PhoneListDrawer from './components/PhoneListDrawer.jsx';
import EmailListDrawer from './components/EmailListDrawer.jsx';

import './wellness-settings-page.css';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const WellnessSettingsPage = () => {
    // -- Session --
    const [campusId, setCampusId] = useState(() => getCampusId());
    const session = useMemo(() => getSession(), []);

    const campusList = useMemo(() => {
        return getCampusList() || [];
    }, []);

    const campusName = useMemo(() => {
        const match = campusList.find((c) => c.campus_id === campusId);
        return match?.campus_name || `Campus ${campusId}`;
    }, [campusId, campusList]);

    // Feature flags for current campus
    const campusData = useMemo(() => {
        return session?.campusData?.[campusId?.toString()] || {};
    }, [session, campusId]);

    const checkinEnabled = !!campusData.is_resident_checkin;
    const roundsEnabled = !!campusData.is_rounding;
    const careflowV2 = !!campusData.careflow_v2;

    // Permissions
    const editRights = useMemo(() => iAmHigherAuthority(20, session?.role_weight), [session]);
    const adminRights = useMemo(() => iAmHigherAuthority(99, session?.role_weight), [session]);
    // Careflow tab — adds/deletes/edits and most assignment drawers require > 80.
    // Below that, users may only edit Voice/Text/Email lists for outputs that
    // are already On (no enabling, no Message After / repeat changes).
    const manageCareflow = useMemo(() => iAmHigherAuthority(30, session?.role_weight), [session]);

    // -- Tab definitions (conditional) --
    const TABS = useMemo(() => {
        const tabs = [];
        if (checkinEnabled) tabs.push({ key: 'checkin', label: 'Check-in' });
        if (roundsEnabled) tabs.push({ key: 'rounds', label: 'Rounds' });
        if (!careflowV2) tabs.push({ key: 'careflow', label: 'Careflow' });
        return tabs;
    }, [checkinEnabled, roundsEnabled, careflowV2]);

    // -- Tab state --
    const [activeTab, setActiveTab] = useState(() => {
        if (checkinEnabled) return 'checkin';
        if (roundsEnabled) return 'rounds';
        return 'careflow';
    });

    // Reset active tab when campus changes and features differ
    useEffect(() => {
        if (checkinEnabled) setActiveTab('checkin');
        else if (roundsEnabled) setActiveTab('rounds');
        else setActiveTab('careflow');
    }, [campusId, checkinEnabled, roundsEnabled]);

    // -- Data stores --
    const [checkinData, setCheckinData] = useState([]);
    const [roundsData, setRoundsData] = useState([]);
    const [residentGroups, setResidentGroups] = useState([]);
    const [areaGroups, setAreaGroups] = useState([]);

    // Careflow data
    const [escData, setEscData] = useState([]);
    const [eventCategories, setEventCategories] = useState([]);
    const [campusSettings, setCampusSettings] = useState(null);
    const [deviceList, setDeviceList] = useState([]);
    const [mobileGroupList, setMobileGroupList] = useState([]);

    // -- Loading / Saving --
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [restoring, setRestoring] = useState(false);

    // -- Window Drawer (Add/Edit) --
    const [windowDrawerOpen, setWindowDrawerOpen] = useState(false);
    const [windowEditTarget, setWindowEditTarget] = useState(null);
    const [windowDrawerTab, setWindowDrawerTab] = useState('checkin');

    // -- Group Assign Drawer --
    const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
    const [groupDrawerTarget, setGroupDrawerTarget] = useState(null);
    const [groupDrawerTab, setGroupDrawerTab] = useState('checkin');

    // -- Delete Confirm Dialog --
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteIds, setDeleteIds] = useState([]);
    const [deleteTab, setDeleteTab] = useState('checkin');

    // -- Careflow Drawers --
    const [globalDrawerOpen, setGlobalDrawerOpen] = useState(false);
    const [cfDrawerOpen, setCfDrawerOpen] = useState(false);
    const [cfEditTarget, setCfEditTarget] = useState(null);
    const [cfSubTab, setCfSubTab] = useState('alert');
    const [cfActiveSubTab, setCfActiveSubTab] = useState('alert');
    const [phoneDrawerOpen, setPhoneDrawerOpen] = useState(false);
    const [phoneDrawerTarget, setPhoneDrawerTarget] = useState(null);
    const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
    const [emailDrawerTarget, setEmailDrawerTarget] = useState(null);
    const [cfGroupDrawerOpen, setCfGroupDrawerOpen] = useState(false);
    const [cfGroupDrawerTarget, setCfGroupDrawerTarget] = useState(null);
    const [cfGroupType, setCfGroupType] = useState('mobile');
    const [cfDeviceDrawerOpen, setCfDeviceDrawerOpen] = useState(false);
    const [cfDeviceDrawerTarget, setCfDeviceDrawerTarget] = useState(null);
    const [cfRefGroupDrawerOpen, setCfRefGroupDrawerOpen] = useState(false);
    const [cfRefGroupDrawerTarget, setCfRefGroupDrawerTarget] = useState(null);
    const [cfRefGroupType, setCfRefGroupType] = useState('resident');
    const [offsiteDialogOpen, setOffsiteDialogOpen] = useState(false);
    const [offsiteDialogTarget, setOffsiteDialogTarget] = useState(null);
    const [offsiteDelay, setOffsiteDelay] = useState(0);

    // -- Careflow Delete Dialog (separate from window deletes) --
    const [cfDeleteDialogOpen, setCfDeleteDialogOpen] = useState(false);
    const [cfDeleteIds, setCfDeleteIds] = useState([]);

    // -- Toast --
    const { toast, showToast: showTypedToast, dismissToast } = useToast();
    /* This page's calls are (message, type); the shared hook is (type, message). */
    const showToast = useCallback((message, type = 'success') => showTypedToast(type, message), [showTypedToast]);

    // ======================================================================
    // Data loading
    // ======================================================================
    const loadData = useCallback(async (cId) => {
        setLoading(true);
        try {
            const cData = session?.campusData?.[cId?.toString()] || {};
            const isCheckin = !!cData.is_resident_checkin;
            const isRounds = !!cData.is_rounding;
            const needsCareflow = !cData.careflow_v2;

            const [
                checkinRes, roundsRes, resGroupsRes, areaGroupsRes,
                escRes, eventCatRes, campusRes, devRes, mobileRes,
            ] = await Promise.all([
                isCheckin ? fetchCheckinWindows(cId) : Promise.resolve([]),
                isRounds ? fetchRoundsWindows(cId) : Promise.resolve([]),
                fetchResidentGroups(cId),
                fetchAreaGroups(cId),
                needsCareflow ? fetchEscalationData(cId) : Promise.resolve([]),
                needsCareflow ? fetchEventCategories(cId) : Promise.resolve([]),
                needsCareflow ? fetchCampusSettings(cId) : Promise.resolve([]),
                needsCareflow ? fetchDevices(cId) : Promise.resolve([]),
                needsCareflow ? fetchMobileGroups(cId) : Promise.resolve([]),
            ]);
            setCheckinData(checkinRes || []);
            setRoundsData(roundsRes || []);
            setResidentGroups(resGroupsRes || []);
            setAreaGroups(areaGroupsRes || []);

            // Careflow data
            setEscData(escRes || []);
            setEventCategories(eventCatRes || []);
            // fetchCampusSettings returns an array — extract the first row
            const campusRow = Array.isArray(campusRes) ? campusRes[0] || null : campusRes;
            setCampusSettings(campusRow);
            setDeviceList(devRes || []);
            setMobileGroupList(mobileRes || []);
        } catch (err) {
            console.error('[WellnessSettingsPage] Load error:', err);
            showToast('Failed to load wellness settings data.', 'error');
        } finally {
            setLoading(false);
        }
    }, [session, showToast]);

    // Initial load + reload on campus switch
    useEffect(() => {
        loadData(campusId);
    }, [campusId, loadData]);

    // ======================================================================
    // Campus switch
    // ======================================================================
    const handleCampusChange = (newId) => {
        setCampusId(newId);
        updateSessionCampus(newId);

        const oldIsCloud = session?.campusData?.[campusId?.toString()]?.is_cloud;
        const newIsCloud = session?.campusData?.[newId?.toString()]?.is_cloud;
        if (oldIsCloud !== newIsCloud) {
            window.location.reload();
        }
    };

    // ======================================================================
    // MQTT Operations — Sync Local & Restore Data
    // ======================================================================
    const handleSyncLocal = async () => {
        setSyncing(true);
        try {
            const [ok] = await apiMqtt({
                operation: 'reboot-server',
                campusId,
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Sync triggered. Please allow up to 3 minutes for changes to take effect.');
            } else {
                showToast('Error syncing. Contact support if the error persists.', 'error');
            }
        } catch {
            showToast('Error syncing. Contact support if the error persists.', 'error');
        } finally {
            setSyncing(false);
        }
    };

    const handleRestoreData = async () => {
        setRestoring(true);
        try {
            const [ok] = await apiMqtt({
                operation: 'restore-server',
                campusId,
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Restore triggered. Please allow up to 3 minutes for changes to take effect.');
            } else {
                showToast('Error restoring. Contact support if the error persists.', 'error');
            }
        } catch {
            showToast('Error restoring. Contact support if the error persists.', 'error');
        } finally {
            setRestoring(false);
        }
    };

    // ======================================================================
    // CRUD Handlers
    // ======================================================================
    const meta = useMemo(() => ({
        campusId,
        userId: getUserId(),
        clientId: getSessionId(),
    }), [campusId]);

    // --- Add Window ---
    const handleAddWindow = async (formData) => {
        setSaving(true);
        const isCheckinTab = windowDrawerTab === 'checkin';
        const payload = [formData];

        try {
            const [ok] = isCheckinTab
                ? await addCheckinWindow(payload, meta)
                : await addRoundsWindow(payload, meta);

            if (ok) {
                setWindowDrawerOpen(false);
                showToast(`${isCheckinTab ? 'Check-in' : 'Rounds'} window added.`);
                await loadData(campusId);
            } else {
                showToast(`Failed to add ${isCheckinTab ? 'check-in' : 'rounds'} window.`, 'error');
            }
        } catch {
            showToast(`Failed to add ${isCheckinTab ? 'check-in' : 'rounds'} window.`, 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Edit Window ---
    const handleEditWindow = async (formData) => {
        setSaving(true);
        const isCheckinTab = windowDrawerTab === 'checkin';
        const payload = [formData];

        // Build oldPayload from the current edit target
        const old = windowEditTarget || {};
        const oldPayload = [{
            window_id: old.window_id,
            description: old.description,
            window_start: old.window_start,
            window_end: old.window_end,
            generate_alarm: old.generate_alarm,
            alarm_at: old.alarm_at,
            ignore_until: old.ignore_until,
            campus_id: campusId,
            window_frequency_id: old.window_frequency_id || 1,
            ...(isCheckinTab ? {} : { interval: old.interval }),
        }];

        try {
            const [ok] = isCheckinTab
                ? await editCheckinWindows(payload, oldPayload, meta)
                : await editRoundsWindows(payload, oldPayload, meta);

            if (ok) {
                setWindowDrawerOpen(false);
                setWindowEditTarget(null);
                showToast(`${isCheckinTab ? 'Check-in' : 'Rounds'} window updated.`);
                await loadData(campusId);
            } else {
                showToast(`Failed to update ${isCheckinTab ? 'check-in' : 'rounds'} window.`, 'error');
            }
        } catch {
            showToast(`Failed to update ${isCheckinTab ? 'check-in' : 'rounds'} window.`, 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Save Groups ---
    const handleSaveGroups = async (windowId, selectedGroupIds) => {
        setSaving(true);
        const isCheckinTab = groupDrawerTab === 'checkin';
        // The backend expects arrays for resident_group_ids / area_ids and
        // handles its own coercion. selectedGroupIds is already an array;
        // old values come back as comma-delimited strings, so split + filter
        // empties out.
        const newGroupIds = Array.isArray(selectedGroupIds) ? selectedGroupIds : [];

        const payload = [{
            window_id: windowId,
            campus_id: campusId,
            ...(isCheckinTab
                ? { resident_group_ids: newGroupIds }
                : { area_ids: newGroupIds }),
        }];

        const oldTarget = groupDrawerTarget || {};
        const oldGroupStr = isCheckinTab
            ? (oldTarget.resident_group_id_list || '')
            : (oldTarget.area_ids || '');
        const oldGroupIds = String(oldGroupStr).split(',').map((s) => s.trim()).filter(Boolean);
        const oldPayload = [{
            window_id: windowId,
            campus_id: campusId,
            ...(isCheckinTab
                ? { resident_group_ids: oldGroupIds }
                : { area_ids: oldGroupIds }),
        }];

        try {
            const [ok] = isCheckinTab
                ? await editCheckinGroups(payload, oldPayload, meta)
                : await editRoundsGroups(payload, oldPayload, meta);

            if (ok) {
                setGroupDrawerOpen(false);
                setGroupDrawerTarget(null);
                showToast('Group assignments updated.');
                await loadData(campusId);
            } else {
                showToast('Failed to update group assignments.', 'error');
            }
        } catch {
            showToast('Failed to update group assignments.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Delete Windows ---
    const handleRequestDelete = (windowIds, tab) => {
        setDeleteIds(windowIds);
        setDeleteTab(tab || activeTab);
        setDeleteDialogOpen(true);
    };

    /* Stays open until the request settles, so the Deleting… state is visible
       and a failure leaves the user where they were instead of dismissing the
       dialog out from under them. */
    const handleConfirmDelete = async () => {
        setSaving(true);
        try {
            const [ok] = await deleteWindows(deleteIds, meta);
            if (ok) {
                showToast(`${deleteIds.length} window${deleteIds.length !== 1 ? 's' : ''} deleted.`);
                setDeleteDialogOpen(false);
                setDeleteIds([]);
                await loadData(campusId);
            } else {
                showToast('Failed to delete window(s).', 'error');
            }
        } catch {
            showToast('Failed to delete window(s).', 'error');
        } finally {
            setSaving(false);
        }
    };

    // ======================================================================
    // Careflow CRUD Handlers
    // ======================================================================

    // --- Save Global Outputs ---
    const handleSaveGlobalOutputs = async (formData, oldPayload) => {
        if (!manageCareflow) {
            showToast('You do not have permission to edit global output settings.', 'error');
            return;
        }
        setSaving(true);
        try {
            const [ok] = await editGlobalOutputs([formData], [oldPayload], meta);
            if (ok) {
                setGlobalDrawerOpen(false);
                showToast('Global output settings updated.');
                await loadData(campusId);
            } else {
                showToast('Failed to update global output settings.', 'error');
            }
        } catch {
            showToast('Failed to update global output settings.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Add Careflow ---
    const handleAddCareflow = async (formData) => {
        if (!manageCareflow) {
            showToast('You do not have permission to add careflows.', 'error');
            return;
        }
        setSaving(true);
        try {
            const [ok] = await addCareflow([formData], meta);
            if (ok) {
                setCfDrawerOpen(false);
                showToast('Careflow added.');
                await loadData(campusId);
            } else {
                showToast('Failed to add careflow.', 'error');
            }
        } catch {
            showToast('Failed to add careflow.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Edit Careflow ---
    const handleEditCareflow = async (formData, oldPayload) => {
        if (!manageCareflow) {
            showToast('You do not have permission to edit careflows.', 'error');
            return;
        }
        setSaving(true);
        try {
            const [ok] = await editCareflow([formData], [oldPayload], meta);
            if (ok) {
                setCfDrawerOpen(false);
                setCfEditTarget(null);
                showToast('Careflow updated.');
                await loadData(campusId);
            } else {
                showToast('Failed to update careflow.', 'error');
            }
        } catch {
            showToast('Failed to update careflow.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Delete Careflows ---
    const handleRequestCfDelete = (careflowIds) => {
        if (!manageCareflow) {
            showToast('You do not have permission to delete careflows.', 'error');
            return;
        }
        setCfDeleteIds(careflowIds);
        setCfDeleteDialogOpen(true);
    };

    const handleConfirmCfDelete = async () => {
        /* Re-checked on confirm, not just at open time: the dialog can outlive a
           session or role change. */
        if (!manageCareflow) {
            setCfDeleteDialogOpen(false);
            showToast('You do not have permission to delete careflows.', 'error');
            return;
        }
        setSaving(true);
        try {
            const [ok] = await deleteCareflows(cfDeleteIds, meta);
            if (ok) {
                showToast(`${cfDeleteIds.length} careflow${cfDeleteIds.length !== 1 ? 's' : ''} deleted.`);
                setCfDeleteDialogOpen(false);
                setCfDeleteIds([]);
                await loadData(campusId);
            } else {
                showToast('Failed to delete careflow(s).', 'error');
            }
        } catch {
            showToast('Failed to delete careflow(s).', 'error');
        } finally {
            setSaving(false);
        }
    };

    // Map outputKey → master enabled flag on the careflow record. Used to
    // auto-enable an output when the user saves assignments via a clicked Off
    // badge — the badge entry point is the only place a save can target a
    // disabled output, since the kebab hides assign actions for disabled ones.
    const OUTPUT_MASTER_FLAG_FIELDS = {
        palmobile: 'mobile_enabled',
        textmessages: 'text_enabled',
        voicemessages: 'voice_enabled',
        email: 'email_enabled',
        pager: 'pager_enabled',
        offsitemonitoring: 'offsite_enabled',
    };

    // Build the careflow record payload mirroring its current state — used to
    // flip a single master enabled flag without touching anything else.
    // The read SP returns the category as `event_category_id`; the write SP
    // expects `escalation_event_id` on the same column. Coalesce so alert
    // careflows don't get dropped to event_category_id=1 (Default Emergency).
    const buildCareflowRecordPayload = useCallback((row, overrides = {}) => ({
        escalation_entity_id: row.escalation_entity_id,
        escalation_event_id: row.escalation_event_id ?? row.event_category_id ?? 1,
        careflow_id: row.careflow_id,
        references: row.references || '',
        enabled: row.enabled,
        enable_start: row.enable_start,
        enable_end: row.enable_end,
        careflow_description: row.careflow_description,
        mobile_enabled: row.outputs?.palmobile?.enabled ? 1 : 0,
        text_enabled: row.outputs?.textmessages?.enabled ? 1 : 0,
        voice_enabled: row.outputs?.voicemessages?.enabled ? 1 : 0,
        email_enabled: row.outputs?.email?.enabled ? 1 : 0,
        pager_enabled: row.outputs?.pager?.enabled ? 1 : 0,
        reporting_enabled: row.outputs?.reporting?.enabled ? 1 : 0,
        offsite_enabled: row.outputs?.offsitemonitoring?.enabled ? 1 : 0,
        mute_enabled: row.outputs?.mute?.enabled ? 1 : 0,
        escalation_type_id: row.escalation_type_id,
        campus_id: campusId,
        ...overrides,
    }), [campusId]);

    // --- Save Careflow Output (mobile/pager groups, phones, emails, offsite) ---
    const handleSaveCareflowOutput = async (outputData, targetRow, outputKey) => {
        const output = targetRow?.outputs?.[outputKey] || {};
        const wasDisabled = !output.enabled;
        /* The drawers report intent; this function owns what it means. Absent (the offsite
           dialog) keeps the previous always-enable behaviour. */
        const wantEnabled = outputData.enabled !== false;
        const turningOff = !wantEnabled;
        // Defense-in-depth: low-privilege users may only edit text/voice/email
        // lists for outputs that are already On. They cannot enable an output
        // or touch any other output type.
        const lowPrivAllowed = ['textmessages', 'voicemessages', 'email'].includes(outputKey) && !wasDisabled;
        if (!manageCareflow && !lowPrivAllowed) {
            showToast('You do not have permission to perform this action.', 'error');
            return;
        }
        /* Defense-in-depth: the toggle is hidden for these users, but a hidden control is
           not a permission check. Turning an output off is a careflow-level change. */
        if (turningOff && !manageCareflow) {
            showToast('You do not have permission to turn outputs off.', 'error');
            return;
        }

        // Guard: a recipient-based output (mobile/text/voice/email/pager) may not be
        // left enabled with zero recipients — it looks configured but never fires.
        // Saving through this handler always ends with the output enabled (reaching
        // it via an Off badge auto-enables the master flag), so block an empty list
        // outright. The user must add a recipient, or turn the output off in Edit.
        const RECIPIENT_OUTPUT_LABELS = {
            palmobile: 'Mobile', textmessages: 'Text', voicemessages: 'Voice', email: 'Email', pager: 'Pager',
        };
        const intendedRefs = Array.isArray(outputData.reference)
            ? outputData.reference.filter(Boolean)
            : String(outputData.reference || '').split(',').map((s) => s.trim()).filter(Boolean);
        /* Only applies while the output is staying on. Turning it off with an empty list is
           the whole point of the toggle — the old message sent people to Edit Careflow. */
        if (wantEnabled && RECIPIENT_OUTPUT_LABELS[outputKey] && intendedRefs.length === 0) {
            const noun = outputKey === 'email' ? 'email address'
                : (outputKey === 'palmobile' || outputKey === 'pager') ? 'group' : 'phone number';
            showToast(`${RECIPIENT_OUTPUT_LABELS[outputKey]} must have at least one ${noun} to stay enabled. Add one, or switch Output Enabled off.`, 'error');
            return;
        }

        setSaving(true);
        try {
            const masterFlagField = OUTPUT_MASTER_FLAG_FIELDS[outputKey];

            const payload = [{
                escalation_child_id: output.escalation_child_id || outputData.escalation_child_id,
                /* MUST be sent explicitly. careflowOutputEdit defaults a missing `enabled`
                   to 1, and sp_editCareflowOutputs writes the same
                   core_escalation_child.enabled column that the master-flag write below
                   sets to 0 — so omitting it turned the output straight back on, leaving
                   it enabled with the recipients we had just cleared. */
                enabled: wantEnabled ? 1 : 0,
                /* Turning off clears recipients only — event_time, send_next_message and
                   send_max are preserved so re-enabling does not mean reconfiguring the
                   timing from scratch. */
                reference: turningOff
                    ? ''
                    : (Array.isArray(outputData.reference) ? outputData.reference.join(',') : (outputData.reference || '')),
                event_time: outputData.event_time ?? output.event_time ?? 0,
                send_next_message: outputData.send_next_message ?? output.send_next_message ?? 0,
                send_max: outputData.send_max ?? output.send_max ?? false,
                campus_id: campusId,
            }];

            const oldPayload = [{
                escalation_child_id: output.escalation_child_id,
                enabled: output.enabled ? 1 : 0,
                reference: output.reference || '',
                event_time: output.event_time || 0,
                send_next_message: output.send_next_message || 0,
                send_max: output.send_max || false,
                campus_id: campusId,
            }];

            /* Turning off flips the master flag BEFORE clearing recipients, the reverse of
               the enable path. If the second write fails, the output is left off with stale
               recipients — harmless, and self-correcting on the next save. Clearing first
               would risk leaving it ON with zero recipients: the exact state the guard
               above exists to prevent, and one that looks configured but never fires. */
            if (turningOff && masterFlagField) {
                const oldRecord = buildCareflowRecordPayload(targetRow);
                const newRecord = { ...oldRecord, [masterFlagField]: 0 };
                const [flagOk] = await editCareflow([newRecord], [oldRecord], meta);
                if (!flagOk) {
                    showToast('Failed to turn the output off.', 'error');
                    return;
                }
            }

            const [ok] = await editCareflowOutput(payload, oldPayload, meta);
            if (ok) {
                // Auto-enable master flag if the user reached this save via a
                // clicked Off badge. Saving assignments to a disabled output
                // would otherwise leave it dark, defeating the click.
                // Skipped for low-privilege users — they cannot toggle outputs.
                if (wantEnabled && wasDisabled && masterFlagField && manageCareflow) {
                    const oldRecord = buildCareflowRecordPayload(targetRow);
                    const newRecord = { ...oldRecord, [masterFlagField]: 1 };
                    await editCareflow([newRecord], [oldRecord], meta);
                }

                setPhoneDrawerOpen(false);
                setPhoneDrawerTarget(null);
                setEmailDrawerOpen(false);
                setEmailDrawerTarget(null);
                setCfGroupDrawerOpen(false);
                setCfGroupDrawerTarget(null);
                setOffsiteDialogOpen(false);
                setOffsiteDialogTarget(null);
                showToast(turningOff
                    ? 'Output turned off and recipients cleared.'
                    : (wasDisabled && masterFlagField ? 'Output enabled and updated.' : 'Output updated.'));
                await loadData(campusId);
            } else {
                showToast('Failed to update output.', 'error');
            }
        } catch {
            showToast('Failed to update output.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Save Phone Numbers (text/voice) ---
    const handleSavePhones = (outputData) => {
        const targetRow = phoneDrawerTarget?.row;
        const outputKey = phoneDrawerTarget?.outputType;
        if (targetRow && outputKey) {
            handleSaveCareflowOutput(outputData, targetRow, outputKey);
        }
    };

    // --- Save Emails ---
    const handleSaveEmails = (outputData) => {
        const targetRow = emailDrawerTarget;
        if (targetRow) {
            handleSaveCareflowOutput(outputData, targetRow, 'email');
        }
    };

    // --- Save Careflow Group Assignment (mobile/pager) ---
    const handleSaveCfGroups = (_targetId, selectedGroupIds, timingData) => {
        const targetRow = cfGroupDrawerTarget;
        const outputKey = cfGroupType === 'mobile' ? 'palmobile' : 'pager';
        if (targetRow) {
            const output = targetRow.outputs?.[outputKey] || {};
            handleSaveCareflowOutput({
                escalation_child_id: output.escalation_child_id || 0,
                reference: selectedGroupIds,
                event_time: timingData?.eventTime ?? output.event_time ?? 0,
                send_next_message: timingData?.sendNextMessage ?? output.send_next_message ?? 0,
                send_max: timingData?.sendMax ?? output.send_max ?? false,
                /* Absent for the reference-group and window uses of this drawer, which
                   have no output to toggle — undefined reads as "stay enabled". */
                enabled: timingData?.enabled,
            }, targetRow, outputKey);
        }
    };

    // --- Save Careflow Device Assignment ---
    const handleSaveCfDevices = async (targetRow, selectedDeviceIds) => {
        if (!targetRow) return;
        if (!manageCareflow) {
            showToast('You do not have permission to assign careflow devices.', 'error');
            return;
        }
        setSaving(true);
        try {
            const newReferences = selectedDeviceIds.join(',');
            const payload = [{
                escalation_entity_id: targetRow.escalation_entity_id,
                escalation_event_id: targetRow.escalation_event_id || 1,
                careflow_id: targetRow.careflow_id,
                references: newReferences,
                enabled: targetRow.enabled,
                enable_start: targetRow.enable_start,
                enable_end: targetRow.enable_end,
                careflow_description: targetRow.careflow_description,
                mobile_enabled: targetRow.outputs?.palmobile?.enabled ? 1 : 0,
                text_enabled: targetRow.outputs?.textmessages?.enabled ? 1 : 0,
                voice_enabled: targetRow.outputs?.voicemessages?.enabled ? 1 : 0,
                email_enabled: targetRow.outputs?.email?.enabled ? 1 : 0,
                pager_enabled: targetRow.outputs?.pager?.enabled ? 1 : 0,
                reporting_enabled: targetRow.outputs?.reporting?.enabled ? 1 : 0,
                offsite_enabled: targetRow.outputs?.offsitemonitoring?.enabled ? 1 : 0,
                mute_enabled: targetRow.outputs?.mute?.enabled ? 1 : 0,
                escalation_type_id: targetRow.escalation_type_id,
                campus_id: campusId,
            }];
            const oldPayload = [{
                escalation_entity_id: targetRow.escalation_entity_id,
                escalation_event_id: targetRow.escalation_event_id || 1,
                careflow_id: targetRow.careflow_id,
                references: targetRow.references || '',
                enabled: targetRow.enabled,
                enable_start: targetRow.enable_start,
                enable_end: targetRow.enable_end,
                careflow_description: targetRow.careflow_description,
                mobile_enabled: targetRow.outputs?.palmobile?.enabled ? 1 : 0,
                text_enabled: targetRow.outputs?.textmessages?.enabled ? 1 : 0,
                voice_enabled: targetRow.outputs?.voicemessages?.enabled ? 1 : 0,
                email_enabled: targetRow.outputs?.email?.enabled ? 1 : 0,
                pager_enabled: targetRow.outputs?.pager?.enabled ? 1 : 0,
                reporting_enabled: targetRow.outputs?.reporting?.enabled ? 1 : 0,
                offsite_enabled: targetRow.outputs?.offsitemonitoring?.enabled ? 1 : 0,
                mute_enabled: targetRow.outputs?.mute?.enabled ? 1 : 0,
                escalation_type_id: targetRow.escalation_type_id,
                campus_id: campusId,
            }];
            const [ok] = await editCareflow(payload, oldPayload, meta);
            if (ok) {
                setCfDeviceDrawerOpen(false);
                setCfDeviceDrawerTarget(null);
                showToast('Careflow devices updated.');
                await loadData(campusId);
            } else {
                showToast('Failed to update devices.', 'error');
            }
        } catch {
            showToast('Failed to update devices.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Save Careflow Reference Groups (resident/area) — reuses editCareflow for references ---
    const handleSaveCfRefGroups = async (_targetId, selectedGroupIds) => {
        const targetRow = cfRefGroupDrawerTarget;
        if (!targetRow) return;
        if (!manageCareflow) {
            showToast('You do not have permission to assign careflow groups.', 'error');
            return;
        }
        setSaving(true);
        try {
            const newReferences = selectedGroupIds.join(',');
            const payload = [{
                escalation_entity_id: targetRow.escalation_entity_id,
                escalation_event_id: targetRow.escalation_event_id || 1,
                careflow_id: targetRow.careflow_id,
                references: newReferences,
                enabled: targetRow.enabled,
                enable_start: targetRow.enable_start,
                enable_end: targetRow.enable_end,
                careflow_description: targetRow.careflow_description,
                mobile_enabled: targetRow.outputs?.palmobile?.enabled ? 1 : 0,
                text_enabled: targetRow.outputs?.textmessages?.enabled ? 1 : 0,
                voice_enabled: targetRow.outputs?.voicemessages?.enabled ? 1 : 0,
                email_enabled: targetRow.outputs?.email?.enabled ? 1 : 0,
                pager_enabled: targetRow.outputs?.pager?.enabled ? 1 : 0,
                reporting_enabled: targetRow.outputs?.reporting?.enabled ? 1 : 0,
                offsite_enabled: targetRow.outputs?.offsitemonitoring?.enabled ? 1 : 0,
                mute_enabled: targetRow.outputs?.mute?.enabled ? 1 : 0,
                escalation_type_id: targetRow.escalation_type_id,
                campus_id: campusId,
            }];
            const oldPayload = [{
                escalation_entity_id: targetRow.escalation_entity_id,
                escalation_event_id: targetRow.escalation_event_id || 1,
                careflow_id: targetRow.careflow_id,
                references: targetRow.references || '',
                enabled: targetRow.enabled,
                enable_start: targetRow.enable_start,
                enable_end: targetRow.enable_end,
                careflow_description: targetRow.careflow_description,
                mobile_enabled: targetRow.outputs?.palmobile?.enabled ? 1 : 0,
                text_enabled: targetRow.outputs?.textmessages?.enabled ? 1 : 0,
                voice_enabled: targetRow.outputs?.voicemessages?.enabled ? 1 : 0,
                email_enabled: targetRow.outputs?.email?.enabled ? 1 : 0,
                pager_enabled: targetRow.outputs?.pager?.enabled ? 1 : 0,
                reporting_enabled: targetRow.outputs?.reporting?.enabled ? 1 : 0,
                offsite_enabled: targetRow.outputs?.offsitemonitoring?.enabled ? 1 : 0,
                mute_enabled: targetRow.outputs?.mute?.enabled ? 1 : 0,
                escalation_type_id: targetRow.escalation_type_id,
                campus_id: campusId,
            }];
            const [ok] = await editCareflow(payload, oldPayload, meta);
            if (ok) {
                setCfRefGroupDrawerOpen(false);
                setCfRefGroupDrawerTarget(null);
                showToast(`${cfRefGroupType === 'resident' ? 'Resident' : 'Area'} groups updated.`);
                await loadData(campusId);
            } else {
                showToast('Failed to update groups.', 'error');
            }
        } catch {
            showToast('Failed to update groups.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Save Offsite Delay ---
    const handleSaveOffsiteDelay = () => {
        const targetRow = offsiteDialogTarget;
        if (targetRow) {
            handleSaveCareflowOutput({
                escalation_child_id: targetRow.outputs?.offsitemonitoring?.escalation_child_id || 0,
                reference: targetRow.outputs?.offsitemonitoring?.reference || '',
                event_time: parseInt(offsiteDelay) * 60,
                send_next_message: targetRow.outputs?.offsitemonitoring?.send_next_message || 0,
                send_max: targetRow.outputs?.offsitemonitoring?.send_max || false,
            }, targetRow, 'offsitemonitoring');
        }
    };

    // ======================================================================
    // Render helpers
    // ======================================================================
    const renderTabContent = () => {
        if (loading) {
            return (
                <div className="pp-section">
                    <div className="pp-section__body">
                        <div className="ss-skeleton" style={{ width: '60%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '80%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px' }} />
                    </div>
                </div>
            );
        }

        switch (activeTab) {
            case 'checkin':
                return (
                    <CheckinTab
                        data={checkinData}
                        residentGroups={residentGroups}
                        onAdd={() => {
                            setWindowEditTarget(null);
                            setWindowDrawerTab('checkin');
                            setWindowDrawerOpen(true);
                        }}
                        onEdit={(win) => {
                            setWindowEditTarget(win);
                            setWindowDrawerTab('checkin');
                            setWindowDrawerOpen(true);
                        }}
                        onAssignGroups={(win) => {
                            setGroupDrawerTarget(win);
                            setGroupDrawerTab('checkin');
                            setGroupDrawerOpen(true);
                        }}
                        onDelete={(ids) => handleRequestDelete(ids, 'checkin')}
                        saving={saving}
                        myRoleWeight={session?.role_weight}
                    />
                );
            case 'rounds':
                return (
                    <RoundsTab
                        data={roundsData}
                        areaGroups={areaGroups}
                        onAdd={() => {
                            setWindowEditTarget(null);
                            setWindowDrawerTab('rounds');
                            setWindowDrawerOpen(true);
                        }}
                        onEdit={(win) => {
                            setWindowEditTarget(win);
                            setWindowDrawerTab('rounds');
                            setWindowDrawerOpen(true);
                        }}
                        onAssignGroups={(win) => {
                            setGroupDrawerTarget(win);
                            setGroupDrawerTab('rounds');
                            setGroupDrawerOpen(true);
                        }}
                        onDelete={(ids) => handleRequestDelete(ids, 'rounds')}
                        saving={saving}
                        myRoleWeight={session?.role_weight}
                    />
                );
            case 'careflow':
                return (
                    <CareflowTab
                        escData={escData}
                        eventCategories={eventCategories}
                        campusSettings={campusSettings}
                        activeSubTab={cfActiveSubTab}
                        onSubTabChange={setCfActiveSubTab}
                        devices={deviceList}
                        mobileGroups={mobileGroupList}
                        residentGroups={residentGroups}
                        areaGroups={areaGroups}
                        canManageCareflow={manageCareflow}
                        onOpenGlobalSettings={() => setGlobalDrawerOpen(true)}
                        onAdd={(subTab) => { setCfEditTarget(null); setCfSubTab(subTab); setCfDrawerOpen(true); }}
                        onEdit={(row, subTab) => { setCfEditTarget(row); setCfSubTab(subTab); setCfDrawerOpen(true); }}
                        onDelete={(ids) => handleRequestCfDelete(ids)}
                        onAssignDevices={(row) => { setCfDeviceDrawerTarget(row); setCfDeviceDrawerOpen(true); }}
                        onAssignResidentGroups={(row) => { setCfRefGroupDrawerTarget(row); setCfRefGroupType('resident'); setCfRefGroupDrawerOpen(true); }}
                        onAssignAreaGroups={(row) => { setCfRefGroupDrawerTarget(row); setCfRefGroupType('area'); setCfRefGroupDrawerOpen(true); }}
                        onAssignMobileGroups={(row) => { setCfGroupDrawerTarget(row); setCfGroupType('mobile'); setCfGroupDrawerOpen(true); }}
                        onAssignPagerGroups={(row) => { setCfGroupDrawerTarget(row); setCfGroupType('pager'); setCfGroupDrawerOpen(true); }}
                        onAssignTextPhones={(row) => { setPhoneDrawerTarget({ row, outputType: 'textmessages' }); setPhoneDrawerOpen(true); }}
                        onAssignVoicePhones={(row) => { setPhoneDrawerTarget({ row, outputType: 'voicemessages' }); setPhoneDrawerOpen(true); }}
                        onAssignEmails={(row) => { setEmailDrawerTarget(row); setEmailDrawerOpen(true); }}
                        onSetOffsiteDelay={(row) => {
                            setOffsiteDialogTarget(row);
                            setOffsiteDelay(row.outputs?.offsitemonitoring?.event_time ? Math.round(row.outputs.offsitemonitoring.event_time / 60) : 0);
                            setOffsiteDialogOpen(true);
                        }}
                        saving={saving}
                    />
                );
            default:
                return null;
        }
    };

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ss-page">
                    {/* Toast notification */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Page header */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <nav className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Settings</span>
                                    <span className="ps-breadcrumb__separator">/</span>
                                    <span className="ps-breadcrumb__current">Wellness Settings</span>
                                </nav>
                                <div className="ps-campus-row">
                                    {/* MQTT action buttons */}
                                    {editRights ? (
                                        <button
                                            className="ap-btn ap-btn--secondary"
                                            onClick={handleSyncLocal}
                                            disabled={syncing}
                                            title="Syncs local server with cloud data, will cause the server to go offline momentarily during the process"
                                        >
                                            {syncing ? 'Syncing...' : 'Sync Local'}
                                        </button>
                                    ) : null}
                                    {/*{adminRights ? (*/}
                                    {/*    <button*/}
                                    {/*        className="ap-btn ap-btn--secondary"*/}
                                    {/*        onClick={handleRestoreData}*/}
                                    {/*        disabled={restoring}*/}
                                    {/*        title="Restore data performs the sync local reboot, however deletes the database file triggering a re-create."*/}
                                    {/*    >*/}
                                    {/*        {restoring ? 'Restoring...' : 'Restore Data'}*/}
                                    {/*    </button>*/}
                                    {/*) : null}*/}

                                    {/* Campus selector */}
                                    <label htmlFor="ws-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="ws-campus-selector"
                                            value={campusId}
                                            onChange={(e) => handleCampusChange(parseInt(e.target.value, 10))}
                                            className="ps-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>
                                                    {c.campus_name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusName}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab bar */}
                    {TABS.length > 0 ? (
                        <div className="pp-tab-bar">
                            {TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    ) : null}

                    {/* Scrollable tab content */}
                    <div className="pp-tab-content">
                        {TABS.length > 0 ? renderTabContent() : (
                            <div className="pp-section">
                                <div className="pp-section__body" style={{ textAlign: 'center', padding: 'var(--space-8, 2rem)' }}>
                                    <p style={{ color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                                        No wellness features are enabled for this campus.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Window Drawer (Add/Edit) */}
                    <WindowDrawer
                        open={windowDrawerOpen}
                        onClose={() => { setWindowDrawerOpen(false); setWindowEditTarget(null); }}
                        onSave={windowEditTarget ? handleEditWindow : handleAddWindow}
                        saving={saving}
                        editTarget={windowEditTarget}
                        tabType={windowDrawerTab}
                        campusId={campusId}
                    />

                    {/* Group Assign Drawer */}
                    <GroupAssignDrawer
                        open={groupDrawerOpen}
                        onClose={() => { setGroupDrawerOpen(false); setGroupDrawerTarget(null); }}
                        onSave={handleSaveGroups}
                        saving={saving}
                        windowTarget={groupDrawerTarget}
                        groups={groupDrawerTab === 'checkin' ? residentGroups : areaGroups}
                        groupType={groupDrawerTab === 'checkin' ? 'resident' : 'area'}
                    />

                    {/* Delete Confirmation (Check-in / Rounds windows) — requires
                        typing "confirm". `deleteTab` tells us which kind, so the
                        wording can name it instead of saying "window(s)". */}
                    {deleteDialogOpen ? (() => {
                        const copy = describeWellnessDelete(
                            deleteTab === 'rounds' ? 'rounds' : 'checkin',
                            { count: deleteIds.length }
                        );
                        return (
                            <ConfirmTypedDialog
                                title={copy.title}
                                confirmLabel={copy.confirmLabel}
                                savingLabel={copy.savingLabel}
                                elevated
                                onClose={() => setDeleteDialogOpen(false)}
                                onConfirm={handleConfirmDelete}
                                saving={saving}
                            >
                                <p style={{ margin: 0, lineHeight: 1.6 }}>
                                    Are you sure you want to delete{' '}
                                    <strong>
                                        {deleteIds.length} {copy.noun}{deleteIds.length !== 1 ? 's' : ''}
                                    </strong>
                                    ?
                                </p>
                                {/* The operational warning — deliberately not muted. */}
                                <p style={{ margin: 'var(--space-3) 0 0', lineHeight: 1.6 }}>
                                    {copy.consequence}
                                </p>
                                <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                                    {copy.closing}
                                </p>
                            </ConfirmTypedDialog>
                        );
                    })() : null}

                    {/* ============================================ */}
                    {/* Careflow Drawers & Dialogs                   */}
                    {/* ============================================ */}

                    {/* Global Output Settings Drawer */}
                    <GlobalOutputDrawer
                        open={globalDrawerOpen}
                        onClose={() => setGlobalDrawerOpen(false)}
                        onSave={handleSaveGlobalOutputs}
                        saving={saving}
                        campusSettings={campusSettings}
                        campusId={campusId}
                    />

                    {/* Careflow Add/Edit Drawer */}
                    <CareflowDrawer
                        open={cfDrawerOpen}
                        onClose={() => { setCfDrawerOpen(false); setCfEditTarget(null); }}
                        onSave={cfEditTarget ? handleEditCareflow : handleAddCareflow}
                        saving={saving}
                        editTarget={cfEditTarget}
                        subTabKey={cfSubTab}
                        eventCategories={eventCategories}
                        campusSettings={campusSettings}
                        campusId={campusId}
                        devices={deviceList}
                        residentGroups={residentGroups}
                        areaGroups={areaGroups}
                        mobileGroups={mobileGroupList}
                    />

                    {/* Phone List Drawer (text/voice) */}
                    <PhoneListDrawer
                        canToggleOutput={manageCareflow}
                        open={phoneDrawerOpen}
                        onClose={() => { setPhoneDrawerOpen(false); setPhoneDrawerTarget(null); }}
                        onSave={handleSavePhones}
                        saving={saving}
                        target={phoneDrawerTarget}
                        campusId={campusId}
                        canEditTiming={manageCareflow}
                    />

                    {/* Email List Drawer */}
                    <EmailListDrawer
                        canToggleOutput={manageCareflow}
                        open={emailDrawerOpen}
                        onClose={() => { setEmailDrawerOpen(false); setEmailDrawerTarget(null); }}
                        onSave={handleSaveEmails}
                        saving={saving}
                        target={emailDrawerTarget}
                        campusId={campusId}
                        canEditTiming={manageCareflow}
                    />

                    {/* Careflow Group Assign Drawer (mobile/pager) */}
                    {/* Careflow mobile/pager output — the only use of this drawer with a
                        master enabled flag, so the only one that offers the toggle. */}
                    <GroupAssignDrawer
                        showEnabledToggle
                        canToggleOutput={manageCareflow}
                        open={cfGroupDrawerOpen}
                        onClose={() => { setCfGroupDrawerOpen(false); setCfGroupDrawerTarget(null); }}
                        onSave={handleSaveCfGroups}
                        saving={saving}
                        windowTarget={cfGroupDrawerTarget}
                        groups={mobileGroupList}
                        groupType={cfGroupType}
                        initialIds={
                            cfGroupDrawerTarget
                                ? (cfGroupDrawerTarget.outputs?.[cfGroupType === 'mobile' ? 'palmobile' : 'pager']?.reference || '').split(',').filter(Boolean)
                                : []
                        }
                    />

                    {/* Careflow Device Assign Drawer */}
                    <DeviceAssignDrawer
                        open={cfDeviceDrawerOpen}
                        onClose={() => { setCfDeviceDrawerOpen(false); setCfDeviceDrawerTarget(null); }}
                        onSave={(ids) => handleSaveCfDevices(cfDeviceDrawerTarget, ids)}
                        saving={saving}
                        row={cfDeviceDrawerTarget}
                        idsKey="references"
                        title="Assign Careflow Devices"
                        contextLabel="Careflow"
                        contextName={cfDeviceDrawerTarget?.careflow_description}
                        devices={deviceList}
                    />

                    {/* Careflow Reference Group Assign Drawer (resident/area) */}
                    <GroupAssignDrawer
                        open={cfRefGroupDrawerOpen}
                        onClose={() => { setCfRefGroupDrawerOpen(false); setCfRefGroupDrawerTarget(null); }}
                        onSave={handleSaveCfRefGroups}
                        saving={saving}
                        windowTarget={cfRefGroupDrawerTarget}
                        groups={cfRefGroupType === 'resident' ? residentGroups : areaGroups}
                        groupType={cfRefGroupType}
                        initialIds={
                            cfRefGroupDrawerTarget
                                ? (cfRefGroupDrawerTarget.references || '').split(',').filter(Boolean)
                                : []
                        }
                    />

                    {/* Delete Confirmation (Careflows) — requires typing "confirm". */}
                    {cfDeleteDialogOpen ? (() => {
                        const copy = describeWellnessDelete('careflow', { count: cfDeleteIds.length });
                        return (
                            <ConfirmTypedDialog
                                title={copy.title}
                                confirmLabel={copy.confirmLabel}
                                savingLabel={copy.savingLabel}
                                elevated
                                onClose={() => setCfDeleteDialogOpen(false)}
                                onConfirm={handleConfirmCfDelete}
                                saving={saving}
                            >
                                <p style={{ margin: 0, lineHeight: 1.6 }}>
                                    Are you sure you want to delete{' '}
                                    <strong>
                                        {cfDeleteIds.length} {copy.noun}{cfDeleteIds.length !== 1 ? 's' : ''}
                                    </strong>
                                    ?
                                </p>
                                {/* The operational warning — deliberately not muted. */}
                                <p style={{ margin: 'var(--space-3) 0 0', lineHeight: 1.6 }}>
                                    {copy.consequence}
                                </p>
                                <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                                    {copy.closing}
                                </p>
                            </ConfirmTypedDialog>
                        );
                    })() : null}

                    {/* Offsite Delay Dialog */}
                    {offsiteDialogOpen ? (
                        <div className="pp-dialog-backdrop" onClick={() => setOffsiteDialogOpen(false)}>
                            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
                                <div className="pp-dialog__header">
                                    <h3 className="pp-dialog__title">Set Offsite Delay</h3>
                                    <button className="dp-drawer__close" onClick={() => setOffsiteDialogOpen(false)}>
                                        <i className="fe fe-x" />
                                    </button>
                                </div>
                                <div className="pp-dialog__body">
                                    {offsiteDialogTarget ? (
                                        <p style={{ marginBottom: 'var(--space-3)', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm)' }}>
                                            Careflow: <strong>{offsiteDialogTarget.careflow_description}</strong>
                                        </p>
                                    ) : null}
                                    <div className="ws-offsite-dialog__field">
                                        <label className="ws-offsite-dialog__label" htmlFor="offsite-delay-input">
                                            Delay (minutes)
                                        </label>
                                        <input
                                            id="offsite-delay-input"
                                            className="dp-drawer__input"
                                            type="number"
                                            min="0"
                                            value={offsiteDelay}
                                            onChange={(e) => setOffsiteDelay(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="pp-dialog__footer">
                                    <button className="dp-btn dp-btn--cancel" onClick={() => setOffsiteDialogOpen(false)}>
                                        Cancel
                                    </button>
                                    <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSaveOffsiteDelay}>
                                        {saving ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default WellnessSettingsPage;
